import { useState } from 'react'
import { useAuth } from '@praxis/component-auth'
import { Container } from 'components/container/Container'
import { useUser } from 'components/user-context'
import { UrlParams } from 'enums/url-params'
import { useSearchParams } from 'react-router-dom'
import { useMPMEnv } from 'utils/env'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import { Cell, TableData } from 'utils/data'
import { addHyperlinkToFirstColumn } from './../helpers'
import { getGreenfieldDrillValues } from 'utils/greenfield'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import useRefresh from 'hooks/utils/useRefresh'
import useCountdown from 'hooks/utils/useCountdown'
import { Constants } from 'constants/constants'
import { useSecureRgd } from 'hooks/assets-protection/useSecureRGD'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { translateTableData } from 'locales/utils'

export const SecureNonStore = () => {
    const { t, isEnglish } = useEssentials()
    const { assetsProtection: apKeys } = TRANSLATION_KEYS
    const [params] = useSearchParams()
    const { location } = useUser()
    const env = useMPMEnv()
    const { logout, session } = useAuth()
    const currentLocation =
        params.get(UrlParams.LOCATION_ID) ?? location.shortName

    const [tabsIndex, setTabsIndex] = useState<number>(0)

    const columnWidths = isEnglish
        ? [90, 100, 100, 90, 80]
        : [100, 100, 100, 120, 120]

    const secure = useSecureRgd({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        dynamicSubPath: Constants.GREENFIELD_CARDS.ASSETS_PROTECTION.SECURE,
        filters: getGreenfieldDrillValues(currentLocation),
        prepend_footer_to_rows: true,
    })

    useRefresh(secure.refetch, secure.remove)
    const count = useCountdown(5, logout, secure.error)

    if (secure.isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={10} columns={4} />
            </div>
        )
    }

    if (secure.isError) {
        return (
            <ErrorAlert
                error={secure.error}
                count={count}
                onRetry={() => secure.refetch()}
            />
        )
    }

    const pickColumns = (
        data: TableData,
        columnIndexes: number[],
    ): TableData => {
        const newData = { ...data }
        newData.headings = columnIndexes
            .map((index) => data.headings?.[index])
            .filter((heading): heading is Cell => heading !== undefined)

        newData.rows = newData.rows.map((row) =>
            columnIndexes.map((index) => row[index]),
        )
        newData.footer = columnIndexes
            .map((index) => data.footer?.[index])
            .filter((heading): heading is Cell => heading !== undefined)

        return newData
    }

    const removeRowsWithEmptyObjects = (data: TableData): TableData => {
        const filteredRows = data.rows.filter(
            (row) => !row.some((cell) => Object.keys(cell).length === 0),
        )
        return { ...data, rows: filteredRows }
    }

    let processedData = secure.data
    if (processedData) {
        processedData = removeRowsWithEmptyObjects(processedData)
        processedData = addHyperlinkToFirstColumn(
            processedData,
            currentLocation,
        )
        processedData = pickColumns(processedData, [0, 1, 3, 7, 5])

        processedData = translateTableData({
            t,
            table: processedData,
            translation: {
                [processedData.headings?.[0]?.value || '']:
                    apKeys.secure.location,
                [processedData.headings?.[1]?.value || '']:
                    apKeys.secure.crimesAgainstPeople,
                [processedData.headings?.[2]?.value || '']:
                    apKeys.secure.crimesAgainstProperty,
                [processedData.headings?.[3]?.value || '']:
                    apKeys.secure.disorderlyConducts,
                [processedData.headings?.[4]?.value || '']:
                    apKeys.secure.securityResolution,
            },
        })
    }

    const payload = {
        '': [
            {
                title: '',
                subtitle: '',
                data: {
                    security: processedData,
                },
            },
        ],
    }

    const handleClick = (tabsIndex: number) => {
        setTabsIndex(tabsIndex)
    }

    return (
        <Container role="main">
            <GeneralizedComponent
                payload={payload}
                title={t(apKeys.secure.title)}
                scrollableWidth={true}
                sortable={false}
                widths={columnWidths}
                pinFirstColumn={true}
                parentHandleClick={(_, tabIndex) => handleClick(tabIndex)}
                selectedTabIndex={tabsIndex}
                selectedTimeIndex={0}
                kpiName="assets protection secure non-store"
            />
        </Container>
    )
}
